import { CartAddEvent } from '@theme/events';

class WholesaleOrderPortal extends HTMLElement {
  connectedCallback() {
    this.searchInput = this.querySelector('[data-wholesale-search]');
    this.vendorSelect = this.querySelector('[data-wholesale-vendor]');
    this.activeCategory = 'all';
    this.submitButton = this.querySelector('[data-wholesale-submit]');
    this.status = this.querySelector('[data-wholesale-status]');

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.searchInput?.addEventListener('input', this.filterProducts);
    this.vendorSelect?.addEventListener('change', this.filterProducts);
    this.submitButton?.addEventListener('click', this.addSelectedItems);
    this.setupProductToggles();
    this.sortProductsByRecency();
    this.updateSummary();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onInput);
    this.searchInput?.removeEventListener('input', this.filterProducts);
    this.vendorSelect?.removeEventListener('change', this.filterProducts);
    this.submitButton?.removeEventListener('click', this.addSelectedItems);
  }

  setupProductToggles() {
    if (!document.querySelector('style[data-wholesale-collapse-styles]')) {
      const style = document.createElement('style');
      style.dataset.wholesaleCollapseStyles = '';
      style.textContent = `
        .wholesale-product__collapse-controls { display:flex; align-items:center; justify-content:stretch; min-width:0; }
        .wholesale-product__toggle { width:100%; min-height:46px; display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 14px; border:1px solid var(--color-border); border-radius:8px; background:var(--color-background); color:var(--color-foreground); font:inherit; font-weight:650; text-align:left; cursor:pointer; }
        .wholesale-product__toggle:hover { background:color-mix(in srgb, var(--color-foreground) 6%, var(--color-background)); }
        .wholesale-product__toggle:focus-visible { outline:2px solid var(--color-foreground); outline-offset:2px; }
        .wholesale-product__toggle-icon { flex:0 0 auto; font-size:1.15rem; line-height:1; transition:transform .18s ease; }
        .wholesale-product__toggle[aria-expanded='true'] .wholesale-product__toggle-icon { transform:rotate(180deg); }
        .wholesale-product__variants[data-wholesale-variants] { grid-column:2; }
        .wholesale-product__variants[data-wholesale-variants][hidden] { display:none !important; }
        @media (max-width:749px) { .wholesale-product__collapse-controls,.wholesale-product__variants[data-wholesale-variants] { grid-column:1; } .wholesale-product__toggle { min-height:48px; } }
      `;
      document.head.append(style);
    }

    this.querySelectorAll('[data-wholesale-product]').forEach((product, index) => {
      if (product.querySelector('[data-wholesale-toggle]')) return;
      const variants = product.querySelector('.wholesale-product__variants');
      const identity = product.querySelector('.wholesale-product__identity');
      if (!variants || !identity) return;

      const optionCount = variants.querySelectorAll('.wholesale-variant').length;
      variants.dataset.wholesaleVariants = '';

      // Single-variant products stay fully visible; collapsing only helps products with choices.
      if (optionCount <= 1) {
        variants.hidden = false;
        product.dataset.singleVariant = 'true';
        return;
      }

      const collapsedLabel = `Choose options (${optionCount})`;
      const variantsId = `wholesale-product-options-${index + 1}`;
      variants.id = variants.id || variantsId;
      variants.hidden = true;

      const controls = document.createElement('div');
      controls.className = 'wholesale-product__collapse-controls';
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'wholesale-product__toggle';
      toggle.dataset.wholesaleToggle = '';
      toggle.dataset.collapsedLabel = collapsedLabel;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', variants.id);
      toggle.innerHTML = `<span data-wholesale-toggle-label>${collapsedLabel}</span><span class="wholesale-product__toggle-icon" aria-hidden="true">⌄</span>`;
      controls.append(toggle);
      identity.insertAdjacentElement('afterend', controls);
    });
  }

  setProductExpanded(product, expanded) {
    if (!product || product.dataset.singleVariant === 'true') return;
    const toggle = product.querySelector('[data-wholesale-toggle]');
    const variants = product.querySelector('[data-wholesale-variants]');
    const label = toggle?.querySelector('[data-wholesale-toggle-label]');
    if (!toggle || !variants) return;
    toggle.setAttribute('aria-expanded', String(expanded));
    variants.hidden = !expanded;
    product.classList.toggle('is-expanded', expanded);
    if (label) label.textContent = expanded ? 'Hide options' : toggle.dataset.collapsedLabel || 'Choose options';
  }

  onClick = (event) => {
    const categoryButton = event.target.closest('[data-wholesale-category]');
    if (categoryButton) {
      this.activeCategory = categoryButton.dataset.wholesaleCategory || 'all';
      this.querySelectorAll('[data-wholesale-category]').forEach((button) => { const isActive = button === categoryButton; button.classList.toggle('is-active', isActive); button.setAttribute('aria-pressed', String(isActive)); });
      this.filterProducts(); return;
    }
    const productToggle = event.target.closest('[data-wholesale-toggle]');
    if (productToggle) { const product = productToggle.closest('[data-wholesale-product]'); const isExpanded = productToggle.getAttribute('aria-expanded') === 'true'; this.setProductExpanded(product, !isExpanded); return; }
    const button = event.target.closest('[data-qty-plus], [data-qty-minus]');
    if (!button) return;
    const input = button.parentElement?.querySelector('[data-wholesale-quantity]');
    if (!input) return;
    const step = Number(input.step) || 1; const minimum = Number(input.dataset.orderMinimum) || step; const maximum = input.max ? Number(input.max) : Infinity; const current = Number(input.value) || 0; let next;
    if (button.hasAttribute('data-qty-plus')) { next = current === 0 ? minimum : current + step; next = Math.min(next, maximum); } else { next = current <= minimum ? 0 : Math.max(0, current - step); }
    input.value = String(next); input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  onInput = (event) => {
    if (!event.target.matches('[data-wholesale-quantity]')) return;
    const maximum = event.target.max ? Number(event.target.max) : Infinity; const enteredQuantity = Math.max(0, Number(event.target.value) || 0);
    if (enteredQuantity > maximum) event.target.value = String(maximum);
    if (enteredQuantity > 0) this.setProductExpanded(event.target.closest('[data-wholesale-product]'), true);
    this.updateSummary();
  };

  filterProducts = () => {
    const query = (this.searchInput?.value || '').trim().toLowerCase(); const vendor = this.vendorSelect?.value || ''; const category = this.activeCategory || 'all'; let visible = 0;
    this.querySelectorAll('[data-wholesale-product]').forEach((product) => { const matchesSearch = !query || product.dataset.search.includes(query); const matchesVendor = !vendor || product.dataset.vendor === vendor; const matchesCategory = category === 'all' || product.dataset.category === category; product.hidden = !(matchesSearch && matchesVendor && matchesCategory); if (!product.hidden) visible += 1; });
    const noResults = this.querySelector('[data-wholesale-no-results]'); if (noResults) noResults.hidden = visible > 0;
  };

  sortProductsByRecency() {
    const productList = this.querySelector('[data-wholesale-products]'); if (!productList) return;
    const products = [...productList.querySelectorAll('[data-wholesale-product]')];
    products.sort((a,b) => { const soldOutDifference = Number(a.dataset.soldOut === 'true') - Number(b.dataset.soldOut === 'true'); if (soldOutDifference !== 0) return soldOutDifference; const updatedDifference = Number(b.dataset.updatedAt || 0) - Number(a.dataset.updatedAt || 0); if (updatedDifference !== 0) return updatedDifference; return Number(b.dataset.createdAt || 0) - Number(a.dataset.createdAt || 0); });
    products.forEach((product) => productList.append(product));
  }

  updateSummary() {
    let units=0, subtotal=0; this.querySelectorAll('[data-wholesale-quantity]').forEach((input) => { const quantity=Math.max(0,Number(input.value)||0); units+=quantity; subtotal+=quantity*(Number(input.dataset.price)||0); });
    const unitsOutput=this.querySelector('[data-wholesale-units]'); const subtotalOutput=this.querySelector('[data-wholesale-subtotal]'); if(unitsOutput) unitsOutput.textContent=String(units); if(subtotalOutput) subtotalOutput.textContent=new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency:window.Shopify?.currency?.active||'USD'}).format(subtotal/100); if(this.submitButton) this.submitButton.disabled=units===0;
  }

  addSelectedItems = async () => {
    const items=[...this.querySelectorAll('[data-wholesale-quantity]')].map((input)=>({id:Number(input.dataset.variantId),quantity:Math.max(0,Number(input.value)||0)})).filter((item)=>item.quantity>0); if(!items.length)return;
    this.submitButton.disabled=true; this.submitButton.setAttribute('aria-busy','true'); this.status.textContent='Adding selected items…';
    try { const sectionIds=[...document.querySelectorAll('cart-items-component[data-section-id]')].map((component)=>component.dataset.sectionId).filter(Boolean); const response=await fetch(this.dataset.cartAddUrl,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections:sectionIds.join(','),sections_url:window.location.pathname})}); const result=await response.json(); if(!response.ok||result.status)throw new Error(result.description||result.message||'Unable to add these items.'); const cart=await fetch(`${window.Shopify.routes.root}cart.js`).then((cartResponse)=>cartResponse.json()); this.querySelectorAll('[data-wholesale-quantity]').forEach((input)=>{input.value='0';}); this.querySelectorAll('[data-wholesale-product]').forEach((product)=>this.setProductExpanded(product,false)); this.updateSummary(); this.status.innerHTML=`Added ${items.reduce((sum,item)=>sum+item.quantity,0)} units. <a href="${this.dataset.cartUrl}">View cart</a>`; this.dispatchEvent(new CartAddEvent(cart,'wholesale-portal',{source:'wholesale-portal',itemCount:items.reduce((sum,item)=>sum+item.quantity,0),sections:result.sections})); }
    catch(error){ this.status.textContent=error instanceof Error?error.message:'Unable to add the selected items.'; this.submitButton.disabled=false; }
    finally { this.submitButton.removeAttribute('aria-busy'); }
  };
}

if (!customElements.get('wholesale-order-portal')) customElements.define('wholesale-order-portal', WholesaleOrderPortal);
